import { useEffect, useState } from "react";
import { X, Gift, Warning } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { usePublicPricing, fmt$ } from "@/lib/usePublicPricing";

/**
 * GrantAccessModal — super-admin only. Opens from the People-and-Silos tab.
 * Feb 16 2026 (Randy pt-comp-grant-system) — see /app/memory/COMP_GRANT_SYSTEM_SPEC.md.
 *
 * Grants a user free access with 4 knobs: monthly-dues waiver, Creator Pro
 * (bundle | individual SKUs | none), LLM voucher gift, and expiration.
 * Requires a reason (audit) and a "GRANT" confirm phrase.
 */
export default function GrantAccessModal({ targetUser, onClose, onGranted }) {
  // Oct 3 2026 (Randy) — this modal told the operator the dues waiver covered
  // "the $10/mo platform subscription". Live from PricingLab now.
  const pricing = usePublicPricing();
  const platformPrice = pricing.platform_fee_cents
    ? `${fmt$(pricing.platform_fee_cents)}/mo`
    : "monthly";
  // Randy's approved answers → sensible defaults: dues waived + full bundle + permanent.
  const [duesWaived, setDuesWaived] = useState(true);
  const [creatorProMode, setCreatorProMode] = useState("bundle"); // "bundle" | "individual" | "none"
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [voucherGeminiUsd, setVoucherGeminiUsd] = useState("");
  const [voucherAnthropicUsd, setVoucherAnthropicUsd] = useState("");
  const [voucherOpenaiUsd, setVoucherOpenaiUsd] = useState("");
  const [expirationMode, setExpirationMode] = useState("never"); // "never" | "date"
  const [expiresAt, setExpiresAt] = useState("");
  const [reason, setReason] = useState("");
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [catalog, setCatalog] = useState(null); // Creator Pro SKU catalog
  const [reservoir, setReservoir] = useState({ gemini: 0, anthropic: 0, openai: 0 });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, r] = await Promise.all([
          api.get("/admin/creator-pro/catalog").catch(() => ({ data: null })),
          api.get("/admin/voucher-reservoir").catch(() => ({ data: { gemini: 0, anthropic: 0, openai: 0 } })),
        ]);
        if (!alive) return;
        setCatalog(c.data);
        setReservoir(r.data);
      } catch (err) {
        console.warn("[GrantAccessModal] catalog/reservoir load failed", err);
      }
    })();
    return () => { alive = false; };
  }, []);

  const skusFromCatalog = (() => {
    if (!catalog) return [];
    const out = [];
    const walk = (node) => {
      if (!node) return;
      if (Array.isArray(node)) { node.forEach(walk); return; }
      if (typeof node !== "object") return;
      if (node.sku_key && node.title) out.push({ key: node.sku_key, title: node.title });
      Object.values(node).forEach(walk);
    };
    walk(catalog);
    // Dedupe
    const seen = new Set();
    return out.filter((s) => (seen.has(s.key) ? false : (seen.add(s.key), true)));
  })();

  const toggleSku = (key) => {
    setSelectedSkus((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const canSubmit = (() => {
    if (submitting) return false;
    if (confirmPhrase !== "GRANT") return false;
    if (!reason.trim() || reason.trim().length < 5) return false;
    const hasBenefit = duesWaived
      || (creatorProMode === "bundle")
      || (creatorProMode === "individual" && selectedSkus.length > 0)
      || Number(voucherGeminiUsd || 0) > 0
      || Number(voucherAnthropicUsd || 0) > 0
      || Number(voucherOpenaiUsd || 0) > 0;
    if (!hasBenefit) return false;
    if (expirationMode === "date" && !expiresAt) return false;
    return true;
  })();

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        reason: reason.trim(),
        expires_at: expirationMode === "date" ? new Date(expiresAt + "T23:59:59Z").toISOString() : null,
        dues_waived: !!duesWaived,
        granted_full_bundle: creatorProMode === "bundle",
        granted_skus: creatorProMode === "individual" ? selectedSkus : [],
        voucher_cents_gemini: Math.round(Number(voucherGeminiUsd || 0) * 100),
        voucher_cents_anthropic: Math.round(Number(voucherAnthropicUsd || 0) * 100),
        voucher_cents_openai: Math.round(Number(voucherOpenaiUsd || 0) * 100),
        confirm: confirmPhrase,
      };
      const r = await api.post(`/admin/users/${targetUser.id}/comp`, payload);
      toast.success(`Access granted to ${targetUser.email}`);
      onGranted?.(r.data);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Grant failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         data-testid="grant-access-modal-backdrop" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto"
           data-testid="grant-access-modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b hairline">
          <div className="flex items-center gap-2">
            <Gift size={20} weight="duotone" className="text-[#4A5D4E]" />
            <div>
              <h2 className="font-serif-display text-xl">Grant Access</h2>
              <div className="text-xs text-[#6B6A68]" data-testid="grant-target-email">{targetUser?.email}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="grant-close" className="p-1 text-[#6B6A68] hover:text-[#1C1B1A]">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Section 1 — Dues */}
          <section>
            <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] mb-2">Monthly dues</div>
            <label className="flex items-start gap-2 cursor-pointer" data-testid="grant-dues-waived-wrap">
              <input type="checkbox" checked={duesWaived} onChange={(e) => setDuesWaived(e.target.checked)}
                     data-testid="grant-dues-waived" className="mt-1" />
              <div>
                <div className="text-sm text-[#1C1B1A]">Waive monthly subscription dues</div>
                <div className="text-xs text-[#6B6A68] mt-0.5">
                  {duesWaived
                    ? `User does not need to pay the ${platformPrice} platform subscription while this comp is active.`
                    : "User will still be required to pay their monthly subscription — a set-up alert will appear on their dashboard until they do."}
                </div>
              </div>
            </label>
          </section>

          {/* Section 2 — Creator Pro */}
          <section>
            <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] mb-2">Creator Pro</div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="cp-mode" value="bundle" checked={creatorProMode === "bundle"}
                       onChange={() => setCreatorProMode("bundle")} data-testid="grant-cp-bundle" />
                <span className="text-sm">All-access (full bundle)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="cp-mode" value="individual" checked={creatorProMode === "individual"}
                       onChange={() => setCreatorProMode("individual")} data-testid="grant-cp-individual" />
                <span className="text-sm">Specific SKUs only</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="cp-mode" value="none" checked={creatorProMode === "none"}
                       onChange={() => setCreatorProMode("none")} data-testid="grant-cp-none" />
                <span className="text-sm">None (dues-waiver only)</span>
              </label>
            </div>
            {creatorProMode === "individual" && (
              <div className="mt-3 border hairline rounded-md p-3 max-h-56 overflow-y-auto"
                   data-testid="grant-cp-sku-list">
                {skusFromCatalog.length === 0 && (
                  <div className="text-xs text-[#6B6A68]">Loading SKUs…</div>
                )}
                {skusFromCatalog.map((s) => (
                  <label key={s.key} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                    <input type="checkbox" checked={selectedSkus.includes(s.key)}
                           onChange={() => toggleSku(s.key)}
                           data-testid={`grant-cp-sku-${s.key}`} />
                    <span>{s.title} <span className="text-xs text-[#9A9893]">({s.key})</span></span>
                  </label>
                ))}
              </div>
            )}
          </section>

          {/* Section 3 — LLM Voucher */}
          <section>
            <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] mb-2">LLM voucher gift (optional)</div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { key: "gemini", label: "Gemini", value: voucherGeminiUsd, setter: setVoucherGeminiUsd, poolCents: reservoir.gemini },
                { key: "anthropic", label: "Claude", value: voucherAnthropicUsd, setter: setVoucherAnthropicUsd, poolCents: reservoir.anthropic },
                { key: "openai", label: "OpenAI", value: voucherOpenaiUsd, setter: setVoucherOpenaiUsd, poolCents: reservoir.openai },
              ].map((p) => (
                <div key={p.key}>
                  <label className="text-xs text-[#6B6A68] block mb-1">{p.label}</label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-[#9A9893]">$</span>
                    <input type="number" min="0" step="0.01" value={p.value}
                           onChange={(e) => p.setter(e.target.value)}
                           data-testid={`grant-voucher-${p.key}`}
                           className="w-full pl-5 pr-2 py-1.5 border hairline rounded-md text-sm"
                           placeholder="0.00" />
                  </div>
                  <div className="text-[10px] text-[#9A9893] mt-1">
                    Reservoir: ${(p.poolCents / 100).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-xs text-[#6B6A68] mt-2">
              Voucher gifts draw from the platform LLM reservoir (bookkeeping). No Stripe fee — actual cost is provider API charges when the user makes calls.
            </div>
          </section>

          {/* Section 4 — Expiration */}
          <section>
            <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] mb-2">Expiration</div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="exp-mode" value="never" checked={expirationMode === "never"}
                       onChange={() => setExpirationMode("never")} data-testid="grant-exp-never" />
                <span className="text-sm">Never (permanent)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="exp-mode" value="date" checked={expirationMode === "date"}
                       onChange={() => setExpirationMode("date")} data-testid="grant-exp-date-mode" />
                <span className="text-sm">Expires on</span>
                {expirationMode === "date" && (
                  <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)}
                         data-testid="grant-exp-date"
                         className="ml-2 px-2 py-1 border hairline rounded-md text-sm" />
                )}
              </label>
            </div>
          </section>

          {/* Section 5 — Reason */}
          <section>
            <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] mb-2">Reason <span className="text-[#C16E5A]">*</span></div>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      data-testid="grant-reason" rows={2}
                      placeholder="e.g. Partnership trade, influencer comp, family/friend gift"
                      className="w-full px-3 py-2 border hairline rounded-md text-sm" />
            <div className="text-xs text-[#6B6A68] mt-1">Recorded in the audit trail. Min 5 characters.</div>
          </section>

          {/* Section 6 — Confirm gate */}
          <section className="border-t hairline pt-4">
            <div className="flex items-start gap-2 text-xs text-[#6B6A68] mb-2">
              <Warning size={14} weight="duotone" className="text-[#C16E5A] mt-0.5" />
              <span>Type <span className="font-mono font-semibold text-[#1C1B1A]">GRANT</span> below to confirm this action.</span>
            </div>
            <input type="text" value={confirmPhrase} onChange={(e) => setConfirmPhrase(e.target.value)}
                   data-testid="grant-confirm"
                   placeholder="Type GRANT"
                   className="w-full px-3 py-2 border hairline rounded-md text-sm font-mono" />
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t hairline bg-[#FAFAF9]">
          <button type="button" onClick={onClose} data-testid="grant-cancel"
                  className="px-4 py-1.5 rounded-md border hairline text-sm text-[#6B6A68] hover:text-[#1C1B1A]">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!canSubmit} data-testid="grant-submit"
                  className="px-4 py-1.5 rounded-md bg-[#4A5D4E] text-white text-sm hover:bg-[#3A4A3E] disabled:opacity-40 disabled:cursor-not-allowed">
            {submitting ? "Granting…" : "Grant access"}
          </button>
        </div>
      </div>
    </div>
  );
}
